<template>
  <div>
    <Header></Header>
    <div class="col-12 d-inline-flex">
      <div class="col-10 no-overflow ms-3 me-3 pt-3" style="position: relative;">
        <div class="page-titlerow">
          <h2>All Matches</h2>
          <button type="button" class="page-helpbtn" :class="{ 'is-open': helpOpen }" :aria-expanded="helpOpen" @click="helpOpen = !helpOpen">
            <svg class="page-helpbtn__chev" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>How this page works
          </button>
        </div>

        <div v-show="helpOpen" class="page-help mb-3">
          <ul>
            <li>Every match across every calendar, paged and sorted by the server (start time by default).</li>
            <li>Open a match in <strong>Daily</strong> to set up its channels and events, or use <strong>Edit</strong> to correct its teams, times, arena or format by hand.</li>
            <li>The Channels and Events columns show the same warnings as Daily.</li>
            <li><strong>Upcoming</strong> (default) shows matches that have not ended yet, <strong>All</strong> also includes finished ones. Use the date window to show only matches overlapping a period, and <strong>Conflicts only</strong> to list just the matches with a conflict. <strong>Next conflict in Daily</strong> opens the day of the next one.</li>
            <li>All times here are in your local time ({{ tz }}).</li>
          </ul>
        </div>

        <div class="box-outline p-2 mb-3 d-flex align-items-center flex-wrap" style="gap: 0.5rem;">
          <span class="small fw-bold">Date window ({{ tz }})</span>
          <label class="small mb-0">From</label>
          <input type="datetime-local" class="form-control form-control-sm" style="width: 180px;" v-model="windowFromLocal">
          <label class="small mb-0">To</label>
          <input type="datetime-local" class="form-control form-control-sm" style="width: 180px;" v-model="windowToLocal">
          <a role="button" class="procricket-btn btn-xs" :class="{ disabled: !canApplyWindow }" @click="applyWindow">Apply</a>
          <a v-if="hasWindow" role="button" class="procricket-btn--reverse btn-xs" @click="clearWindow">Show all</a>
          <span v-if="hasWindow" class="small text-muted">Showing only matches that overlap this window.</span>
          <!-- Upcoming by default so the list opens on the next match instead of the oldest one -
               All also includes matches that have already ended. -->
          <div class="view-switch ms-auto" role="group" aria-label="Matches to show" title="Upcoming = matches that have not ended yet">
            <a role="button" :class="{ active: onlyUpcoming }" @click="setUpcoming(true)">Upcoming</a>
            <a role="button" :class="{ active: !onlyUpcoming }" @click="setUpcoming(false)">All</a>
          </div>
          <!-- Every conflicting match, paged like the full list - with Upcoming ticked, the row count
               matches the "(N)" on the "Next conflict in Daily" button. -->
          <label class="small mb-0 d-flex align-items-center" style="gap: 0.3rem;">
            <input type="checkbox" v-model="onlyConflicts" @change="onFilterChange"> Conflicts only
          </label>
        </div>

        <div class="d-flex justify-content-between align-items-center matches-toolbar">
          <div class="d-flex align-items-center toolbar-btns toolbar-btns--compact" style="gap: 0.75rem;">
            <a role="button" class="procricket-btn--reverse mb-3" @click="backToCalendars">Back to Calendars</a>
            <!-- Opens Daily on the (local) day of the next upcoming conflict - see
                 CalMatchPageResponse.nextConflictAt. -->
            <a role="button" class="procricket-btn--reverse mb-3" :class="{ disabled: !nextConflictAt }"
               :title="nextConflictAt ? 'Open the next day with a channel conflict or double-booked event in Daily' : 'No upcoming conflicts'"
               @click="openNextConflict">
              Next conflict in Daily ({{ conflictMatchCount }})
            </a>
          </div>
          <div class="items-per-page-dropdown mb-3 text-nowrap">
            <label>Items Per Page: </label>
            <select v-model="sizeChoice" @change="updateItemsPerPage">
              <option value="auto">Auto ({{ autoSize }})</option>
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </div>
        </div>

        <div v-if="loadError !== ''" class="error-message mb-3">{{ loadError }}</div>
        <div class="table-responsive">
          <table class="table table-striped table-bordered cal-matches-table all-matches-table">
            <thead>
            <tr>
              <th v-for="col in columns" :key="col.key" :class="col.cls" role="button" @click="toggleSort(col.key)">
                {{ col.label }}<span class="small">{{ sortIndicator(col.key) }}</span>
              </th>
              <th class="am-channels">Channels</th>
              <th class="am-events">Events</th>
              <th class="am-daily">Daily</th>
              <th class="am-edit">Edit</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="match in calMatchList" :key="match.calMatchId" class="match-summary-row" :class="{ 'row-excluded': !match.active }">
              <!-- Mobile-only card face (same pattern as CalMatches.vue) - tapping it opens Daily. -->
              <td class="mobile-match-card" :colspan="columns.length + 4" @click="openInDaily(match)">
                <div class="mobile-card-top">
                  <span class="mobile-card-teams text-truncate">{{ truncateText(match.team1, 40) }} <span class="mobile-vs">vs</span> {{ truncateText(match.team2, 40) }}</span>
                  <span class="chevron">&#9656;</span>
                </div>
                <div class="mobile-card-meta small text-truncate" v-if="match.venue">{{ match.venue }}</div>
                <div class="mobile-card-meta text-muted small">{{ formatDateTime(match.startTime) }} – {{ formatDateTime(match.endTime) }}</div>
                <div class="mobile-card-meta text-muted small text-truncate">{{ leagueNameFor(match.calLeagueId) }}</div>
                <div class="mobile-card-bottom d-flex flex-wrap" style="gap: 0.3rem;">
                  <span v-if="!match.active" class="badge bg-secondary">Inactive</span>
                  <span v-if="summaryFor(match).channelConflict" class="badge bg-danger">&#9888; Channel conflict</span>
                  <span v-if="summaryFor(match).eventDoubleBooked" class="badge bg-danger">&#9888; Event double-booked</span>
                  <span v-if="summaryFor(match).eventCount === 0" class="badge bg-warning text-dark">No event</span>
                  <span v-else-if="summaryFor(match).applications.length" class="badge bg-secondary">{{ appCountLabel(summaryFor(match)) }}</span>
                  <router-link :to="editLink(match)" class="procricket-btn--reverse btn-xs ms-auto" @click.stop>Edit</router-link>
                </div>
              </td>
              <td class="text-truncate">
                <router-link :to="{ name: 'CalLeagueMatches', params: leagueLinkParams(match.calLeagueId) }">
                  {{ leagueNameFor(match.calLeagueId) }}
                </router-link>
              </td>
              <td class="text-truncate am-small">{{ match.calMatchId }}</td>
              <td class="col-1 team-cell text-truncate">{{ truncateText(match.team1, 40) }}</td>
              <td class="col-1 team-cell text-truncate">{{ truncateText(match.team2, 40) }}</td>
              <td class="team-cell text-truncate">{{ truncateText(match.venue, 40) }}</td>
              <td class="text-nowrap am-small">{{ formatDateTime(match.startTime) }}</td>
              <td class="text-nowrap am-small">{{ formatDateTime(match.endTime) }}</td>
              <td class="text-truncate am-small">{{ truncateText(match.format, 20) }}</td>
              <td class="col-1 text-truncate">{{ truncateText(match.calStatus, 20) }}</td>
              <td class="col-1 text-truncate">{{ match.status }}</td>
              <td class="col-1 text-truncate">{{ match.active }}</td>
              <!-- Same warnings as Daily's match card: a channel conflict, and an event that's missing
                   or double-booked - see CalMatchSetupSummary on the BE. -->
              <td class="col-1 text-nowrap small">
                {{ summaryFor(match).channelCount }}
                <span v-if="summaryFor(match).channelConflict" class="badge bg-danger ms-1"
                      title="A channel here overlaps another match's channel or encoder stream">&#9888; conflict</span>
              </td>
              <td class="col-1 text-nowrap small">
                {{ summaryFor(match).eventCount }}
                <span v-if="summaryFor(match).eventDoubleBooked" class="badge bg-danger ms-1"
                      title="An event here is also assigned to another match in an overlapping time">&#9888; double-booked</span>
                <span v-else-if="summaryFor(match).eventCount === 0" class="badge bg-warning text-dark ms-1"
                      title="Add an event to publish this match">no event</span>
                <span v-if="summaryFor(match).applications.length" class="badge bg-secondary ms-1"
                      :title="summaryFor(match).applications.join(', ')">{{ appCountLabel(summaryFor(match)) }}</span>
              </td>
              <td class="col-1 centered">
                <router-link :to="dailyLink(match)" class="procricket-btn btn-xs">Open</router-link>
              </td>
              <td class="col-1 centered">
                <router-link :to="editLink(match)" class="procricket-btn--reverse btn-xs">Edit</router-link>
              </td>
            </tr>
            <tr v-if="!loading && calMatchList.length === 0">
              <td :colspan="columns.length + 4" class="text-center text-muted">No matches found.</td>
            </tr>
            </tbody>
          </table>
        </div>
        <br>
        <vue-awesome-paginate
            :total-items="totalElements"
            :items-per-page="size"
            :max-pages-shown="5"
            v-model="page"
            :on-click="changePage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import AdminService from "@/services/admin-service";
import Header from "@/components/Header.vue";
import { Sort, CalMatchPageRequest, CalMatchPageFilter } from "@/model/models";
import { fromDateTimeLocal, formatLocalDateTime, localTimezoneLabel, localDateParamFromIso } from "@/utils/general-functions";

const DEFAULT_SORT_COLUMN = 'startTime';

/**
 * All Matches (CalMatchesAll) - one read-only list of every match across every calendar, paged/sorted
 * by the BE the same way Sessions.vue is (POST /cal-league/match/list-paged). Editing channels/events
 * happens in Daily; each row links there. The Edit button opens a match's edit page (CalMatchEdit).
 * The old All Match Events route (/configuration/match-events)
 * redirects here. Each row also shows Daily's warnings, from the response's setupSummaries.
 */
export default {
  name: "AllMatches",
  components: {
    Header
  },
  data() {
    return {
      tz: localTimezoneLabel(),
      helpOpen: false,
      page: 1,
      // 'auto' = as many rows as fit the window; otherwise a fixed page size.
      sizeChoice: 'auto',
      autoSize: this.estimateAutoSize(),
      totalElements: 0,
      calMatchList: [],
      sortColumn: DEFAULT_SORT_COLUMN,
      sortDirection: 'ASC',
      // Applied window (sent to the BE) vs the inputs' datetime-local values.
      windowFrom: '',
      windowTo: '',
      windowFromLocal: '',
      windowToLocal: '',
      // Ticked sends upcomingOnly: true; unticked leaves it out, so past matches show too.
      onlyUpcoming: true,
      // Ticked sends conflictsOnly: true - channel conflicts + double-booked events, same set the count uses.
      onlyConflicts: false,
      conflictMatchCount: 0,
      nextConflictAt: null,
      // { [calMatchId]: CalMatchSetupSummary } for the current page.
      setupSummaryById: {},
      calLeaguesList: [],
      loading: false,
      loadError: '',
      columns: [
        { key: 'calLeagueId', label: 'League', cls: 'am-league' },
        { key: 'calMatchId', label: 'Id', cls: 'am-id' },
        { key: 'team1', label: 'Team 1', cls: 'am-team team-cell' },
        { key: 'team2', label: 'Team 2', cls: 'am-team team-cell' },
        { key: 'venue', label: 'Venue', cls: 'am-venue team-cell' },
        { key: 'startTime', label: 'Start (' + localTimezoneLabel() + ')', cls: 'am-time' },
        { key: 'endTime', label: 'End (' + localTimezoneLabel() + ')', cls: 'am-time' },
        { key: 'format', label: 'Format', cls: 'am-format' },
        { key: 'calStatus', label: 'Cal Status', cls: 'am-status-wide' },
        { key: 'status', label: 'Status', cls: 'am-status' },
        { key: 'active', label: 'Active', cls: 'am-status' }
      ]
    };
  },
  computed: {
    size() {
      return this.sizeChoice === 'auto' ? this.autoSize : Number(this.sizeChoice);
    },
    hasWindow() {
      return !!this.windowFrom && !!this.windowTo;
    },
    canApplyWindow() {
      return !!this.windowFromLocal && !!this.windowToLocal && this.windowFromLocal < this.windowToLocal;
    },
    calLeaguesById() {
      const map = {};
      for (const lg of this.calLeaguesList) map[lg.calLeagueId] = lg;
      return map;
    }
  },
  mounted() {
    window.addEventListener('resize', this.onWindowResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
    clearTimeout(this._resizeTimer);
  },
  created() {
    AdminService.getCalLeaguesList(true).then((leagues) => {
      this.calLeaguesList = leagues || [];
    }).catch((error) => console.error("Error loading leagues:", error));
    this.doLoadData();
  },
  methods: {
    doLoadData() {
      const request = new CalMatchPageRequest(
          this.page - 1,
          this.size,
          new Sort(this.sortColumn, this.sortDirection),
          new CalMatchPageFilter(
              this.hasWindow ? this.windowFrom : undefined,
              this.hasWindow ? this.windowTo : undefined,
              this.onlyUpcoming ? true : undefined,
              this.onlyConflicts ? true : undefined
          )
      );
      this.loading = true;
      this.loadError = '';
      AdminService.getCalMatchesPaged(request).then((data) => {
        this.calMatchList = data.calMatchList || [];
        const summaries = {};
        (data.setupSummaries || []).forEach((s) => { summaries[s.calMatchId] = s; });
        this.setupSummaryById = summaries;
        this.page = data.page + 1;
        this.totalElements = data.totalElements;
        this.conflictMatchCount = data.conflictMatchCount || 0;
        this.nextConflictAt = data.nextConflictAt || null;
        this.$nextTick(this.refineAutoSize);
      }).catch((error) => {
        console.error("Error loading matches:", error);
        this.loadError = this.extractErrorMessage(error);
      }).finally(() => {
        this.loading = false;
      });
    },
    // Rough guess before any row exists: header block above the table is ~330px, a row ~44px.
    estimateAutoSize() {
      if (window.innerWidth <= 768) return 20;
      return this.clampSize(Math.floor((window.innerHeight - 330 - 48 - 90) / 44));
    },
    clampSize(n) {
      return Math.max(5, Math.min(100, n || 20));
    },
    // Once rows are on screen, size the page from the real table position and row height.
    measureAutoSize() {
      if (window.innerWidth <= 768) return 20;
      const table = this.$el && this.$el.querySelector('.all-matches-table');
      const row = table && table.querySelector('tbody tr.match-summary-row');
      if (!table || !row) return this.autoSize;
      const head = table.querySelector('thead');
      const top = table.getBoundingClientRect().top + window.scrollY;
      const rowHeight = row.getBoundingClientRect().height || 44;
      const headHeight = head ? head.getBoundingClientRect().height : 48;
      // ~90px under the table for the pager and margins.
      return this.clampSize(Math.floor((window.innerHeight - top - headHeight - 90) / rowHeight));
    },
    refineAutoSize() {
      if (this.sizeChoice !== 'auto' || this._autoRefined) return;
      this._autoRefined = true;
      const next = this.measureAutoSize();
      if (next !== this.autoSize) {
        this.autoSize = next;
        this.page = 1;
        this.doLoadData();
      }
    },
    onWindowResize() {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        if (this.sizeChoice !== 'auto') return;
        const next = this.measureAutoSize();
        if (next !== this.autoSize) {
          this.autoSize = next;
          this.page = 1;
          this.doLoadData();
        }
      }, 300);
    },
    editLink(match) {
      return { name: 'CalMatchEdit', params: { calMatchId: match.calMatchId } };
    },
    // Missing summary (older BE) -> neutral values, so the columns show 0 and no badges instead of breaking.
    summaryFor(match) {
      return this.setupSummaryById[match.calMatchId]
          || { channelCount: 0, eventCount: 0, applications: [], channelConflict: false, eventDoubleBooked: false };
    },
    // "1 app" / "2 apps" - distinct applications this match's events belong to (names in the tooltip).
    appCountLabel(summary) {
      const n = (summary.applications || []).length;
      return `${n} app${n === 1 ? '' : 's'}`;
    },
    changePage(page) {
      this.page = page;
      this.doLoadData();
    },
    updateItemsPerPage() {
      this.page = 1;
      this._autoRefined = false;
      this.doLoadData();
    },
    toggleSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'ASC' ? 'DESC' : 'ASC';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'ASC';
      }
      this.page = 1;
      this.doLoadData();
    },
    sortIndicator(column) {
      if (this.sortColumn !== column) return '';
      return this.sortDirection === 'ASC' ? ' ▲' : ' ▼';
    },
    setUpcoming(value) {
      if (this.onlyUpcoming === value) return;
      this.onlyUpcoming = value;
      this.onFilterChange();
    },
    onFilterChange() {
      this.page = 1;
      this.doLoadData();
    },
    applyWindow() {
      if (!this.canApplyWindow) return;
      this.windowFrom = fromDateTimeLocal(this.windowFromLocal);
      this.windowTo = fromDateTimeLocal(this.windowToLocal);
      this.page = 1;
      this.doLoadData();
    },
    clearWindow() {
      this.windowFrom = '';
      this.windowTo = '';
      this.windowFromLocal = '';
      this.windowToLocal = '';
      this.page = 1;
      this.doLoadData();
    },
    // Daily's day is the viewer's local day, so the match's start is converted to a local date.
    dailyLink(match) {
      return { name: 'Daily', query: { date: localDateParamFromIso(match.startTime), openMatch: String(match.calMatchId) } };
    },
    openInDaily(match) {
      this.$router.push(this.dailyLink(match));
    },
    openNextConflict() {
      if (!this.nextConflictAt) return;
      this.$router.push({ name: 'Daily', query: { date: localDateParamFromIso(this.nextConflictAt) } });
    },
    // "League #5" fallback covers the moment before calLeaguesList has loaded, or a deleted league.
    leagueNameFor(calLeagueId) {
      const lg = this.calLeaguesById[calLeagueId];
      return lg ? lg.name : `League #${calLeagueId}`;
    },
    // Full CalLeague as route params, whitelisted to the per-league routes' own path segments
    // (same as CalLeagues.vue's "Matches"/"Match Events" buttons).
    leagueLinkParams(calLeagueId) {
      const lg = this.calLeaguesById[calLeagueId] || { name: `League #${calLeagueId}` };
      return {
        calLeagueId,
        name: lg.name,
        shortName: lg.shortName,
        sport: lg.sport,
        uid: lg.uid,
        calendarSourceUrl: lg.calendarSourceUrl,
        lastImport: lg.lastImport,
        status: lg.status,
        active: lg.active
      };
    },
    // "2026-09-04T15:30:00Z" -> "2026-09-04 17:30" (local time).
    formatDateTime(iso) {
      return formatLocalDateTime(iso);
    },
    truncateText(text, limit) {
      if (text === undefined || text === null) return null;
      return String(text).length > limit ? String(text).substring(0, limit) + '...' : String(text);
    },
    backToCalendars() {
      this.$router.push({ name: 'CalLeagues' });
    },
    extractErrorMessage(error) {
      if (error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }
      return error.message || 'Something went wrong.';
    }
  }
};
</script>
<style src="../../assets/style.css">
</style>
